import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const LenisContext = createContext<Lenis | null>(null);

/** Access the shared Lenis instance — mainly so feature components know smooth-scroll is active. */
export function useLenis(): Lenis | null {
  return useContext(LenisContext);
}

/**
 * Mounted once at the app root. Skips entirely for prefers-reduced-motion.
 * Also wires Lenis's scroll events into GSAP's ScrollTrigger (its own
 * ticker drives Lenis's raf) — the standard integration recipe for the two
 * libraries, done once here so individual sections using ScrollTrigger
 * (see `useParallax`) don't each need to re-wire it.
 */
export function SmoothScrollProvider({ children }: { children: ReactNode }) {
  const [lenis, setLenis] = useState<Lenis | null>(null);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const instance = new Lenis({ duration: 1.1, smoothWheel: true });
    setLenis(instance);

    const syncLenisWithGsapTicker = (time: number) => instance.raf(time * 1000);

    instance.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(syncLenisWithGsapTicker);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(syncLenisWithGsapTicker);
      instance.destroy();
      setLenis(null);
    };
  }, []);

  return <LenisContext.Provider value={lenis}>{children}</LenisContext.Provider>;
}
